import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { APCM } from 'utils/constants';
import { useCommonInits } from 'hooks/useCommonInits';
import { useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import {
    MenuItem,
    Select,
    Box,
    Paper,
    TextField,
    Typography,
    Button,
    Tooltip,
} from '@mui/material';
import {
    getAPCMEvent,
    getEmail,
    getAPCMFilteredEvent,
} from 'redux/userLog/userLogAction';
import TimeMenu from 'components/TimeMenu';
import { getTimeAttributes } from 'redux/time/timeSlice';
import { getUser, getUnitIds } from 'redux/auth/authSlice';
import {
    getAllGroups,
    getPriceReqRaisedDataByID,
    getAllPriceRequests,
} from 'redux/datasource/dataSourceActions';
import { getGroups } from 'redux/datasource/dataSourceSlice';
import { clearDataSource } from 'redux/userLog/userLogSlice';

import { FileDownload } from '@mui/icons-material';
const ALL = 'ALL';

export const APCMUserEvent = ({}) => {
    const { dispatch, select, translate } = useCommonInits();
    const { currentTime, previousTime } = select(getTimeAttributes);
    const { emails, filteredEvents, events } = useSelector(
        state => state.userLogEvent,
    );
    const user = useSelector(getUser);
    const Groups = useSelector(getGroups);
    const [email, setEmail] = useState(ALL);
    const [groupName, setGroupName] = useState(ALL);
    const [reqName, setReqName] = useState(ALL);
    const [toggle, setToggle] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const units = useSelector(getUnitIds);
    const unitIds = JSON.parse(units);
    const filteredGroups = Groups?.filter(val => {
        return unitIds.find(a => {
            return val.name === a;
        });
    });
    const ITEM_HEIGHT = 70;
    const ITEM_PADDING_TOP = 2;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: '22%',
            },
        },
    };

    useEffect(() => {
        dispatch(getAllGroups());
        dispatch(getAllPriceRequests());
        const params = {
            app_type: [APCM, 'APCM_Authorizer', 'APCM_Executor'],
        };
        dispatch(getEmail(params));
        dispatch(getAPCMEvent({ app_type: [APCM] }));
    }, []);

    useEffect(() => {
        if (user.app_type.includes('APCM')) {
            if (Groups) {
                const params = {
                    group_id: filteredGroups.map(group => group._id),
                };
                dispatch(getPriceReqRaisedDataByID(params));
            }
        }
    }, [Groups]);

    useEffect(() => {
        if (previousTime !== null && currentTime !== null) handleEventChange();
    }, [email, groupName, reqName, previousTime, currentTime]);

    const exportToXlsx = () => {
        const excelData =
            events?.length === 0
                ? filteredEvents.map(event => ({
                      Date_Time: event.date_time,
                      User_Email: event.user_email,
                      Group_Name: event.group_name,
                      Request_Name: event.price_request_name,
                      Event: event.event,
                  }))
                : events.map(event => ({
                      Date_Time: event.date_time,
                      User_Email: event.user_email,
                      Group_Name: event.group_name,
                      Request_Name: event.price_request_name,
                      Event: event.event,
                  }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        const file = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([file]), 'excelFile.xlsx');
    };

    const handleExportXlsx = () => {
        const excelData =
            events?.length === 0
                ? filteredEvents.map(event => ({
                      Date_Time: event.date_time,
                      User_Email: event.user_email,
                      Group_Name: event.group_name,
                      Request_Name: event.price_request_name,
                      Event: event.event,
                  }))
                : events.map(event => ({
                      Date_Time: event.date_time,
                      User_Email: event.user_email,
                      Group_Name: event.group_name,
                      Request_Name: event.price_request_name,
                      Event: event.event,
                  }));

        exportToXlsx(excelData, columns);
    };

    const handleUserChange = e => {
        setEmail(e.target.value);
        setToggle(true);
    };

    const handleGroupNameChange = e => {
        setGroupName(e.target.value);
        setToggle(true);
    };

    const handleReqChange = e => {
        setReqName(e.target.value);
        setToggle(true);
    };

    const handleEventChange = () => {
        const params = {
            email: email,
            app_type: [APCM],
            from: previousTime,
            to: currentTime,
            group_name: groupName,
            price_request_name: reqName,
        };
        if (toggle === true) {
            dispatch(clearDataSource());
        }
        dispatch(getAPCMFilteredEvent(params));
    };

    const columns = useMemo(
        () => [
            {
                field: 'date_time',
                headerName: 'Date Time',
                width: 210,
                sortable: false,
                filterable: false,
                textAlign: 'left',
            },
            {
                field: 'user_email',
                headerName: 'User Email',
                width: 150,
                sortable: false,
                filterable: false,
                textAlign: 'left',
            },
            {
                field: 'group_name',
                headerName: 'Group Name',
                width: 150,
                sortable: false,
                filterable: false,
                textAlign: 'left',
            },
            {
                field: 'price_request_name',
                headerName: 'Request Name',
                width: 150,
                sortable: false,
                filterable: false,
                textAlign: 'left',
            },
            {
                field: 'event',
                headerName: 'Event',
                width: 850,
                sortable: false,
                filterable: false,
                textAlign: 'left',
                renderCell: params => (
                    <Tooltip title={params.row.event}>
                        <span>{params.row.event}</span>
                    </Tooltip>
                ),
            },
        ],
        [],
    );

    const renderTableData = () => {
        return (
            <>
                <DataGrid
                    disableColumnResize
                    disableColumnMenu
                    rows={events?.length === 0 ? filteredEvents : events}
                    columns={columns}
                    getRowId={row => row._id}
                    rowsPerPageOptions={[10, 25, 50]}
                    autoHeight={true}
                    pageSize={pageSize}
                    onPageSizeChange={newPage => setPageSize(newPage)}
                    getRowSpacing={params => ({
                        top: params.isFirstVisible ? 0 : -2,
                        bottom: params.isLastVisible ? 0 : -2,
                    })}
                />
            </>
        );
    };

    return (
        <>
            {' '}
            <Box sx={{ padding: 1 }}>
                <div
                    style={{
                        padding: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant={'h4'}>User Events</Typography>
                </div>
            </Box>
            <Paper sx={styles.container}>
                <div style={styles.margin1}>
                    <div style={{ display: 'flex', justifyContent: 'left' }}>
                        <label>
                            {translate('userEmail')} : &nbsp;
                            <Select
                                size="small"
                                value={email}
                                onChange={handleUserChange}
                                MenuProps={MenuProps}
                            >
                                <MenuItem value={translate('all')}>
                                    {translate('all')}
                                </MenuItem>
                                {emails.map((item, index) => {
                                    return (
                                        <MenuItem
                                            value={item.email}
                                            key={`item-${index}`}
                                        >
                                            {item.email}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </label>
                        &nbsp;&nbsp;
                        <label>
                            Group Name : &nbsp;
                            <Select
                                MenuProps={MenuProps}
                                size="small"
                                value={groupName}
                                onChange={handleGroupNameChange}
                            >
                                <MenuItem value={translate('all')}>
                                    {translate('all')}
                                </MenuItem>
                                {user.app_type.includes('APCM')
                                    ? Groups.map((item, index) => {
                                          return (
                                              <MenuItem
                                                  value={item.name}
                                                  key={`item-${index}`}
                                              >
                                                  {item.name}
                                              </MenuItem>
                                          );
                                      })
                                    : filteredGroups.map((item, index) => {
                                          return (
                                              <MenuItem
                                                  value={item.name}
                                                  key={`item-${index}`}
                                              >
                                                  {item.name}
                                              </MenuItem>
                                          );
                                      })}
                            </Select>
                        </label>
                        &nbsp;&nbsp;
                        <label style={{ marginTop: '8px' }}>
                            Price Request Name :
                        </label>
                        &nbsp;
                        <TextField
                            size="small"
                            type="text"
                            placeholder="Price Request Name"
                            onChange={handleReqChange}
                            value={reqName}
                        />
                    </div>
                    <div style={styles.margin}>
                        <Tooltip title={'Export To Excel'}>
                            <Button
                                onClick={() => handleExportXlsx()}
                                variant="outlined"
                                style={styles.btn}
                            >
                                <FileDownload />
                            </Button>
                        </Tooltip>

                        <TimeMenu></TimeMenu>
                    </div>
                </div>
                <Box sx={styles.container2}>{renderTableData()}</Box>
            </Paper>
        </>
    );
};

const styles = {
    margin: {
        marginLeft: '10px',
        display: 'flex',
        justifyContent: 'space-between',
    },
    margin1: {
        marginLeft: '10px',
        display: 'flex',
        paddingBottom: 10,
        justifyContent: 'space-between',
    },
    dropdown: {
        width: '90px',
        height: '35px',
        display: 'flex!important',
        marginTop: '6px',
    },
    container: {
        p: 2,
        m: 1,
        height: '97%',
        display: 'flex',
        flexDirection: 'column',
    },
    container2: {
        flex: 12,
        overflow: 'auto',
    },
    container3: {
        flex: 1,
        position: 'relative',
    },
    btn: {
        borderRadius: '20px',
    },
};
